import { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Normalize a DB blog post document (snake_case) to the shape expected by
 * legacy React components (camelCase), preserving the original `content` blocks.
 */
export function normalizePost(p) {
  if (!p) return null;
  return {
    ...p,
    metaDescription: p.meta_description ?? p.metaDescription ?? '',
    readTime: p.read_time ?? p.readTime ?? '',
  };
}

/** Hook: fetches blog category list from backend, memoized once per page load. */
export function useBlogCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    axios.get(`${API}/blog/categories`).then((r) => {
      if (cancel) return;
      setCategories(r.data || []);
    }).catch(() => {}).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);
  return { categories, loading };
}

/** Hook: fetches published posts (optionally filtered by category). */
export function useBlogPosts(category) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { limit: 200 };
    if (category && category !== 'tutti') params.category = category;
    axios.get(`${API}/blog`, { params })
      .then((r) => {
        if (cancel) return;
        const list = (r.data?.posts || []).map(normalizePost);
        setPosts(list);
      })
      .catch((e) => !cancel && setError(e))
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [category]);
  return { posts, loading, error };
}

/** Hook: fetches a single blog post by slug (with related). */
export function useBlogPost(slug) {
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [redirect, setRedirect] = useState(null);
  useEffect(() => {
    if (!slug) return;
    let cancel = false;
    setLoading(true); setNotFound(false); setRedirect(null);
    axios.get(`${API}/blog/${slug}`)
      .then((r) => {
        if (cancel) return;
        // Phase 4: honor 301 redirects
        if (r.data?.redirect && r.data?.target) {
          setRedirect(r.data.target);
          return;
        }
        setPost(normalizePost(r.data?.post));
        setRelated((r.data?.related || []).map(normalizePost));
      })
      .catch((e) => {
        if (cancel) return;
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, [slug]);
  return { post, related, loading, notFound, redirect };
}
